import { useReducer, useMemo, useEffect, useCallback, useRef } from 'react'
import { playerReducer, initialState, ACTIONS } from './playerReducer'
import { PLAYER_STATUS } from './constants'
import { getBeatTypeDef } from './beatTypes'
import { createSession, recordBeatVisit, getVisitedBeatIds } from './session'

export function useThreadPlayer(rawBeats, options = {}) {
  const { resetKey, startIndex = 0, onComplete, onBeatChange, onPositionChange } = options

  const beats = useMemo(
    () => (rawBeats || []).filter((b) => b.active !== false).sort((a, b) => a.position - b.position),
    [rawBeats]
  )

  const [state, dispatch] = useReducer(playerReducer, initialState)
  const sessionRef = useRef(createSession())

  // Tracks the last navigation action so position changes can report whether
  // the player arrived sequentially (NEXT) or by jumping (GO_TO/PREV)
  const lastActionRef = useRef(null)

  // Reset when beats change (thread switch via resetKey)
  useEffect(() => {
    dispatch({ type: ACTIONS.SET_BEATS, payload: beats.length })
    sessionRef.current = createSession()
    if (startIndex > 0 && beats.length > 0) {
      dispatch({ type: ACTIONS.GO_TO, payload: startIndex })
    }
  }, [resetKey, beats.length])

  const currentBeat = beats[state.currentIndex] || null
  const currentBeatTypeDef = currentBeat ? getBeatTypeDef(currentBeat.beat_type) : null

  // Keep latest callback refs so the position-change effect always calls the
  // current version without needing the callbacks in its dependency array
  // (avoids stale closures over parent state like generatedContent)
  const onBeatChangeRef = useRef(onBeatChange)
  const onPositionChangeRef = useRef(onPositionChange)
  useEffect(() => { onBeatChangeRef.current = onBeatChange }, [onBeatChange])
  useEffect(() => { onPositionChangeRef.current = onPositionChange }, [onPositionChange])

  // Record visits and fire callbacks
  const prevIndexRef = useRef(state.currentIndex)
  useEffect(() => {
    if (currentBeat && state.currentIndex !== prevIndexRef.current) {
      sessionRef.current = recordBeatVisit(sessionRef.current, currentBeat)
      onBeatChangeRef.current?.(currentBeat, state.currentIndex)
      onPositionChangeRef.current?.(currentBeat, state.currentIndex, { isSequential: lastActionRef.current === 'NEXT' })
      prevIndexRef.current = state.currentIndex
    }
  }, [state.currentIndex, currentBeat])

  // Record initial beat visit
  useEffect(() => {
    if (currentBeat && sessionRef.current.visits.length === 0) {
      sessionRef.current = recordBeatVisit(sessionRef.current, currentBeat)
    }
  }, [currentBeat])

  // Fire onComplete
  useEffect(() => {
    if (state.status === PLAYER_STATUS.COMPLETE) {
      onComplete?.()
    }
  }, [state.status])

  const next = useCallback(() => { lastActionRef.current = 'NEXT'; dispatch({ type: ACTIONS.NEXT }) }, [])
  const prev = useCallback(() => { lastActionRef.current = 'PREV'; dispatch({ type: ACTIONS.PREV }) }, [])
  const goTo = useCallback((index) => { lastActionRef.current = 'GO_TO'; dispatch({ type: ACTIONS.GO_TO, payload: index }) }, [])
  const reset = useCallback(() => dispatch({ type: ACTIONS.RESET }), [])

  const canGoNext = state.currentIndex < beats.length - 1
  const canGoPrev = state.currentIndex > 0
  const isFirst = state.currentIndex === 0
  const isLast = state.currentIndex >= beats.length - 1
  const progress = beats.length > 0 ? ((state.currentIndex + 1) / beats.length) * 100 : 0

  return {
    currentBeat,
    currentIndex: state.currentIndex,
    beats,
    status: state.status,
    currentBeatTypeDef,
    next,
    prev,
    goTo,
    reset,
    canGoNext,
    canGoPrev,
    isFirst,
    isLast,
    progress,
    beatCount: beats.length,
    session: sessionRef.current,
    visitedBeatIds: getVisitedBeatIds(sessionRef.current),
  }
}
